import apiClient from './apiClient';
import { apiHelpers } from './normalizedApiClient';
import { API_ENDPOINTS } from '../config/api';
import { normalizeDataArray, normalizeDataObject } from '../utils/normalizationUtils';

/**
 * Obtiene todas las empresas del usuario actual
 * @returns {Promise<Array>} Lista de empresas del usuario
 */
export const obtenerEmpresasUsuario = async () => {
  try {
    console.log('🏢 [EmpresasService] Obteniendo empresas del usuario actual');
    
    // Usar el cliente normalizado que automáticamente normaliza las respuestas
    const response = await apiHelpers.empresas.get(API_ENDPOINTS.EMPRESAS.BASE);
    
    if (response.data) {
      const empresasData = response.data.data || response.data.empresas || response.data;
      console.log(`✅ [EmpresasService] ${empresasData.length} empresas encontradas (ya normalizadas)`);
      
      return empresasData;
    }
    
    return [];
  } catch (error) {
    console.error('❌ [EmpresasService] Error al obtener empresas del usuario:', error);
    throw error;
  }
};

/**
 * Obtiene todas las empresas (solo para superadmin)
 * @returns {Promise<Array>} Lista de todas las empresas
 */
export const obtenerTodasLasEmpresas = async () => {
  try {
    //console.log('🏢 [EmpresasService] Obteniendo todas las empresas (superadmin)');
    
    // Usar el cliente normalizado
    const response = await apiHelpers.empresas.get(API_ENDPOINTS.EMPRESAS.TODAS);
    
    if (response.data) {
      const empresasData = response.data.data || response.data.empresas || response.data;
      //console.log(`✅ [EmpresasService] ${empresasData.length} empresas encontradas (todas, ya normalizadas)`);
      
      return empresasData;
    }
    
    return [];
  } catch (error) {
    console.error('❌ [EmpresasService] Error al obtener todas las empresas:', error);
    throw error;
  }
};

/**
 * Obtiene una empresa específica por ID
 * @param {string} empresaId - ID de la empresa
 * @returns {Promise<Object>} Datos de la empresa
 */
export const obtenerEmpresaPorId = async (empresaId) => {
  try {
    //console.log(`🏢 [EmpresasService] Obteniendo empresa por ID: ${empresaId}`);
    
    const response = await apiClient.get(API_ENDPOINTS.EMPRESAS.BY_ID(empresaId));
    
    if (response.data) {
      const empresaData = response.data.data || response.data.empresa || response.data;
      //console.log(`✅ [EmpresasService] Empresa encontrada: ${empresaData.Nombre || empresaData.nombre}`);
      return empresaData;
    }
    
    return null;
  } catch (error) {
    console.error(`❌ [EmpresasService] Error al obtener empresa por ID ${empresaId}:`, error);
    throw error;
  }
};

/**
 * Obtiene una empresa por código único
 * @param {string} codigoEmpresa - Código único de la empresa
 * @returns {Promise<Object>} Datos de la empresa
 */
export const obtenerEmpresaPorCodigo = async (codigoEmpresa) => {
  try {
    //console.log(`🏢 [EmpresasService] Obteniendo empresa por código: ${codigoEmpresa}`);
    
    const response = await apiClient.get(`${API_ENDPOINTS.EMPRESAS.BY_CODIGO}?codigo=${encodeURIComponent(codigoEmpresa)}`);
    
    if (response.data) {
      const empresaData = response.data.data || response.data.empresa || response.data;
      //console.log(`✅ [EmpresasService] Empresa encontrada por código: ${empresaData.Nombre || empresaData.nombre}`);
      return empresaData;
    }
    
    return null;
  } catch (error) {
    console.error(`❌ [EmpresasService] Error al obtener empresa por código ${codigoEmpresa}:`, error);
    throw error;
  }
};

/**
 * Crea una nueva empresa
 * @param {Object} empresaData - Datos de la empresa a crear
 * @returns {Promise<Object>} Empresa creada
 */
export const crearEmpresa = async (empresaData) => {
  try {
    //console.log('🏢 [EmpresasService] Creando nueva empresa:', empresaData.nombre);
    
    const response = await apiClient.post(API_ENDPOINTS.EMPRESAS.BASE, empresaData);
    
    if (response.data) {
      const empresaCreada = response.data.data || response.data.empresa || response.data;
      console.log(`✅ [EmpresasService] Empresa creada exitosamente: ${empresaCreada.Nombre || empresaCreada.nombre}`);
      localStorage.setItem('onboarding_completed', true);
      return empresaCreada;
    }
    
    return null;
  } catch (error) {
    console.error('❌ [EmpresasService] Error al crear empresa:', error);
    throw error;
  }
};

/**
 * Actualiza una empresa existente
 * @param {string} empresaId - ID de la empresa
 * @param {Object} updates - Datos a actualizar
 * @returns {Promise<Object>} Empresa actualizada
 */
export const actualizarEmpresa = async (empresaId, updates) => {
  try {
    //console.log(`🏢 [EmpresasService] Actualizando empresa ${empresaId}`);
    
    const response = await apiClient.put(API_ENDPOINTS.EMPRESAS.BY_ID(empresaId), updates);
    
    if (response.data) {
      const empresaActualizada = response.data.data || response.data.empresa || response.data;
      //console.log(`✅ [EmpresasService] Empresa actualizada exitosamente: ${empresaActualizada.Nombre || empresaActualizada.nombre}`);
      return empresaActualizada;
    }
    
    return null;
  } catch (error) {
    console.error(`❌ [EmpresasService] Error al actualizar empresa ${empresaId}:`, error);
    throw error;
  }
};

/**
 * Verifica si un código de empresa está disponible
 * @param {string} codigo - Código a verificar
 * @returns {Promise<boolean>} true si está disponible, false si no
 */
export const verificarCodigoDisponible = async (codigo) => {
  try {
    //console.log(`🏢 [EmpresasService] Verificando disponibilidad de código: ${codigo}`);
    
    const response = await apiClient.get(`${API_ENDPOINTS.EMPRESAS.VERIFICAR_CODIGO}?codigo=${encodeURIComponent(codigo)}`);
    
    if (response.data) {
      const disponible = response.data.data?.disponible ?? response.data.disponible ?? false;
      //console.log(`✅ [EmpresasService] Código ${codigo} ${disponible ? 'disponible' : 'no disponible'}`);
      return disponible;
    }
    
    return false;
  } catch (error) {
    console.error(`❌ [EmpresasService] Error al verificar código ${codigo}:`, error);
    throw error;
  }
};

/**
 * Obtiene los usuarios de una empresa
 * @param {string} empresaId - ID de la empresa
 * @returns {Promise<Array>} Lista de usuarios de la empresa
 */
export const obtenerUsuariosEmpresa = async (empresaId) => {
  try {
    //console.log(`🏢 [EmpresasService] Obteniendo usuarios de empresa ${empresaId}`);
    
    const response = await apiClient.get(API_ENDPOINTS.EMPRESAS.USUARIOS(empresaId));
    
    if (response.data) {
      const usuariosData = response.data.data || response.data.usuarios || response.data;
      //console.log(`✅ [EmpresasService] ${usuariosData.length} usuarios encontrados`);
      return usuariosData;
    }
    
    return [];
  } catch (error) {
    console.error(`❌ [EmpresasService] Error al obtener usuarios de empresa ${empresaId}:`, error);
    throw error;
  }
};

/**
 * Obtiene detalles completos de empresa (alias para obtenerEmpresaPorId)
 * @param {string} empresaId - ID de la empresa
 * @returns {Promise<Object>} Datos completos de la empresa
 */
export const obtenerDetallesEmpresa = async (empresaId) => {
  return obtenerEmpresaPorId(empresaId);
};

// Funciones que requieren implementación adicional en el backend
// Por ahora mantienen stub para compatibilidad

/**
 * Unirse a empresa por código de invitación
 * @param {string} codigoInvitacion - Código de invitación
 * @param {string} clerkUserId - ID del usuario en Clerk
 * @returns {Promise<string>} ID de la empresa
 */
export const joinEmpresaByInvitation = async (codigoInvitacion, clerkUserId) => {
  console.warn('⚠️ [EmpresasService] joinEmpresaByInvitation no migrada aún al backend');
  throw new Error('Función no migrada aún al backend');
};

/**
 * Generar código de invitación
 * @param {string} empresaId - ID de la empresa
 * @param {string} email - Email del invitado
 * @param {string} perfilId - ID del perfil
 * @param {string} sucursalId - ID de la sucursal
 * @param {string} clerkUserId - ID del usuario que invita
 * @returns {Promise<Object>} Datos de la invitación
 */
export const generarInvitacion = async (empresaId, email, perfilId = 'cliente', sucursalId = null, clerkUserId) => {
  console.warn('⚠️ [EmpresasService] generarInvitacion no migrada aún al backend');
  throw new Error('Función no migrada aún al backend');
};

/**
 * Actualizar perfil de usuario en empresa
 * @param {string} empresaId - ID de la empresa
 * @param {string} usuarioId - ID del usuario
 * @param {string} nuevoPerfilId - Nuevo perfil ID
 * @param {string} sucursalId - ID de la sucursal
 * @returns {Promise<Object>} Relación actualizada
 */
export const actualizarPerfilUsuario = async (empresaId, usuarioId, nuevoPerfilId, sucursalId = null) => {
  console.warn('⚠️ [EmpresasService] actualizarPerfilUsuario no migrada aún al backend');
  throw new Error('Función no migrada aún al backend');
};

/**
 * Obtener perfiles disponibles
 * @returns {Promise<Array>} Lista de perfiles
 */
export const obtenerPerfiles = async () => {
  console.warn('⚠️ [EmpresasService] obtenerPerfiles no migrada aún al backend');
  throw new Error('Función no migrada aún al backend');
};

/**
 * Remover usuario de empresa
 * @param {string} empresaId - ID de la empresa
 * @param {string} usuarioId - ID del usuario
 * @returns {Promise<void>}
 */
export const removerUsuarioEmpresa = async (empresaId, usuarioId) => {
  console.warn('⚠️ [EmpresasService] removerUsuarioEmpresa no migrada aún al backend');
  throw new Error('Función no migrada aún al backend');
};

/**
 * Obtener invitaciones pendientes
 * @param {string} empresaId - ID de la empresa
 * @returns {Promise<Array>} Lista de invitaciones
 */
export const obtenerInvitacionesPendientes = async (empresaId) => {
  console.warn('⚠️ [EmpresasService] obtenerInvitacionesPendientes no migrada aún al backend');
  throw new Error('Función no migrada aún al backend');
};

/**
 * Obtener todos los planes disponibles
 * @returns {Promise<Array>} Lista de planes
 */
export const obtenerPlanes = async () => {
  console.warn('⚠️ [EmpresasService] obtenerPlanes no migrada aún al backend');
  throw new Error('Función no migrada aún al backend');
};

/**
 * Obtener detalles de un plan específico
 * @param {string} planId - ID del plan
 * @returns {Promise<Object>} Datos del plan
 */
export const obtenerPlan = async (planId) => {
  console.warn('⚠️ [EmpresasService] obtenerPlan no migrada aún al backend');
  throw new Error('Función no migrada aún al backend');
};

/**
 * Actualizar plan de empresa
 * @param {string} empresaId - ID de la empresa
 * @param {string} planId - ID del nuevo plan
 * @returns {Promise<Object>} Empresa actualizada
 */
export const actualizarPlanEmpresa = async (empresaId, planId) => {
  console.warn('⚠️ [EmpresasService] actualizarPlanEmpresa no migrada aún al backend');
  throw new Error('Función no migrada aún al backend');
};

/**
 * Actualizar estado de suscripción (webhook de Clerk)
 * @param {string} empresaId - ID de la empresa
 * @param {Object} suscripcionData - Datos de la suscripción
 * @returns {Promise<Object>} Empresa actualizada
 */
export const actualizarEstadoSuscripcion = async (empresaId, suscripcionData) => {
  console.warn('⚠️ [EmpresasService] actualizarEstadoSuscripcion no migrada aún al backend');
  throw new Error('Función no migrada aún al backend');
};

/**
 * Verificar límites del plan actual
 * @param {string} empresaId - ID de la empresa
 * @returns {Promise<Object>} Información de límites
 */
export const verificarLimitesPlan = async (empresaId) => {
  console.warn('⚠️ [EmpresasService] verificarLimitesPlan no migrada aún al backend');
  throw new Error('Función no migrada aún al backend');
};

/**
 * Verificar permisos del usuario actual en empresa
 * @param {string} empresaId - ID de la empresa
 * @param {string} accion - Acción a verificar
 * @param {string} recurso - Recurso sobre el que actuar
 * @param {string} clerkUserId - ID del usuario
 * @returns {Promise<boolean>} true si tiene permisos
 */
export const verificarPermisos = async (empresaId, accion, recurso, clerkUserId) => {
  console.warn('⚠️ [EmpresasService] verificarPermisos no migrada aún al backend');
  throw new Error('Función no migrada aún al backend');
};

/**
 * Crear usuario en Supabase si no existe (función interna)
 * @param {Object} clerkUser - Datos del usuario de Clerk
 * @returns {Promise<Object>} Usuario creado o existente
 */
export const crearUsuarioSiNoExiste = async (clerkUser) => {
  console.warn('⚠️ [EmpresasService] crearUsuarioSiNoExiste manejado automáticamente en el backend');
  return null;
};

// Mantener compatibilidad con código existente que usa el objeto empresasApi
export const empresasApi = {
  obtenerEmpresasUsuario,
  obtenerTodasLasEmpresas,
  obtenerEmpresaPorCodigo,
  crearEmpresa,
  actualizarEmpresa,
  obtenerDetallesEmpresa,
  verificarCodigoDisponible,
  obtenerUsuariosEmpresa,
  joinEmpresaByInvitation,
  generarInvitacion,
  actualizarPerfilUsuario,
  obtenerPerfiles,
  removerUsuarioEmpresa,
  obtenerInvitacionesPendientes,
  obtenerPlanes,
  obtenerPlan,
  actualizarPlanEmpresa,
  actualizarEstadoSuscripcion,
  verificarLimitesPlan,
  verificarPermisos,
  crearUsuarioSiNoExiste
};

// Exportación por defecto para compatibilidad
export default empresasApi;